import {
    HttpEvent,
    HttpInterceptor,
    HttpHandler,
    HttpRequest,
    HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { constant } from '../constants/constant';
import { AuthService } from '../services/auth.service';
import { AuthenticationClient, RefreshTokenRequest } from '../api-connector/sso-connector';

@Injectable({
    providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    constructor(
        private authService: AuthService,
        private authClient: AuthenticationClient
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const token = this.authService.getToken();
        let authReq = req;
        if (token) {
            authReq = this.addTokenHeader(req, token);
        }

        return next.handle(authReq).pipe(
            catchError((error) => {
                if (error instanceof HttpErrorResponse && !authReq.url.includes('api/Authentication/RefreshToken') && !authReq.url.includes('api/Authentication/LogOut') && error.status === 401) {
                    return this.handle401Error(authReq, next);
                }
                return throwError(error);
            })
        );
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            const refreshToken = this.authService.getRefreshToken();
            if (refreshToken) {
                return this.authClient
                    .refreshToken(
                        new RefreshTokenRequest({
                            token: this.authService.getToken() ?? undefined,
                            refreshToken: refreshToken ?? undefined,
                        })
                    )
                    .pipe(
                        switchMap((res: any) => {
                            this.isRefreshing = false;
                            if (res && res.token) {
                                this.authService.setToken(res.token);
                                this.authService.setRefreshToken(res.refreshToken);
                                this.refreshTokenSubject.next(res.token);
                                return next.handle(this.addTokenHeader(request, res.token));
                            }
                            this.authService.logout();
                            return throwError('Token refresh failed');
                        }),
                        catchError((err) => {
                            this.isRefreshing = false;
                            this.authService.logout();
                            return throwError(err);
                        })
                    );
            } else {
                this.authService.logout();
                return throwError('No refresh token available');
            }
        }

        return this.refreshTokenSubject.pipe(
            filter((token) => token !== null),
            take(1),
            switchMap((token) => next.handle(this.addTokenHeader(request, token)))
        );
    }

    private addTokenHeader(request: HttpRequest<any>, token: string) {
        return request.clone({
            headers: request.headers.set(constant.TOKEN_HEADER_KEY, token),
        });
    }
}
